/**
 * WeDo Coaching — English translations
 * Professional EN copy, calibrated for a corporate/leadership consulting audience.
 */
const en = {
  common: {
    cta: {
      contact: "Start a conversation",
      contactShort: "Get in touch",
      diagnostic: "Take the diagnostic",
      bookCall: "Book a meeting",
      readMore: "Read article",
      requestInfo: "Request information",
      send: "Send message",
      sending: "Sending…",
      learnMore: "Learn more",
      backToBlog: "Back to the journal",
    },
    referenced: "Referenced by",
    referencedSub: "Industrial · Research · Aerospace · Energy · Services",
    quote: "\"A company's challenges are never purely human or purely technical.\"",
    confidential: "Your information remains confidential. We reply within 24 business hours.",
    loading: "Loading…",
    emptyBlog: "No articles published yet.",
  },

  nav: {
    expertises: "Our expertise",
    about: "About",
    team: "Team",
    references: "Case studies",
    blog: "Journal",
    diagnostic: "Diagnostic",
    contact: "Contact",
    expertiseList: [
      { label: "Executives & ExCom", desc: "Executive coaching, collective work", path: "/dirigeants-codir" },
      { label: "Management & Leadership", desc: "Posture, autonomy, communication, standards", path: "/management-leadership" },
      { label: "Management Academy", desc: "Sustainable managerial academies & journeys", path: "/ecole-management" },
      { label: "COO / Operational Advisory", desc: "Structuring, growth, multi-site", path: "/coo-conseil" },
      { label: "Seminars & Transformation", desc: "Executive seminars, collective transformation", path: "/seminaires" },
      { label: "PCM & Dynamics", desc: "Process Communication, relational leadership", path: "/pcm" },
    ],
  },

  footer: {
    pitch: "Wedo works on the real-life functioning of organizations.",
    colAccompaniments: "Engagements",
    colExpertises: "Expertise",
    colContact: "Contact",
    legal: "Legal notice",
    admin: "Admin",
    rights: "All rights reserved.",
    interventions: "France · Europe · International (FR / EN)",
  },

  home: {
    hero: {
      eyebrow: "Leadership · ExCom · Management · Transformation · Organization",
      line1: "Decide better.",
      line2: "Cooperate better.",
      line3a: "Empower ",
      line3b: "better.",
      lede: "Wedo supports executives, managers and organizations in clarifying how they function, strengthening collective leadership and structuring more sustainable ways of working together. Decisions, governance, management, coordination, responsibilities, collective transformation: we step in where human, managerial and organizational stakes truly meet.",
    },
    pillars: {
      eyebrow: "Our approach",
      titleA: "Transform organizations.",
      titleB: "Strengthen leadership.",
      lede: "A field-born approach. Twelve years in operations leadership, in ExCom, in environments of growth, structuring and multi-site management. Wedo now supports organizations on what truly conditions their functioning: quality of decisions, clarity of roles, managerial posture, cooperation and the ability to evolve over time.",
      items: [
        { label: "Leadership & ExCom", desc: "Align decisions and strengthen the leadership team." },
        { label: "Management & Leadership", desc: "Develop solid, accountable and structuring managers." },
        { label: "Advisory & Structuring", desc: "Support growth and operational functioning." },
        { label: "Seminars & Transformation", desc: "Build collective moments that genuinely transform practices." },
      ],
    },
    figures: {
      eyebrow: "The experience behind the brand",
      title: "A proven approach.",
      items: [
        { num: "12", suffix: "yrs", label: "in operations leadership and ExCom" },
        { num: "320", suffix: "+", label: "executives and managers coached to date" },
        { num: "4", suffix: "continents", label: "of intervention across Europe and internationally" },
      ],
    },
    offers: {
      eyebrow: "Areas of intervention",
      titleA: "Work on the ",
      titleAccent1: "systems",
      titleB: " as much as the ",
      titleAccent2: "people",
      cta: "All our engagements",
      items: [
        { tag: "Executives & ExCom", title: "Align those who decide", desc: "Executive coaching · ExCom support · strategic facilitation · diagnostic of collective functioning." },
        { tag: "COO / Operational Advisory", title: "Structure the growth", desc: "Organization, roles, cross-functional coordination, multi-site, scaling. An operator's perspective, not a theorist's." },
        { tag: "Management & Leadership", title: "Equip the managers", desc: "Posture, autonomy, communication, tension management, prioritization. Managers who hold their role." },
        { tag: "Management Academy", title: "Develop managers sustainably", desc: "Managerial academies, leadership, communication, autonomy, relational intelligence and anchoring of practices in daily work." },
      ],
    },
    william: {
      eyebrow: "About",
      titleA: "Before the coaching,",
      titleB: "the operations.",
      caption: "William Marques · Founder",
      role: "Former Operations Director · Certified Coach · PCM",
      p1: "Twelve years in operations leadership. ExCom member. Supported a company growing from 4 to 70+ employees. Structuring, international development, multi-site coordination.",
      p2: "That experience feeds a concrete, systemic approach, deeply rooted in the real functioning of organizations.",
      cta: "See the full journey",
    },
    manifesto: {
      eyebrow: "Our conviction",
      title: "\"Organizations don't slow down for lack of talent. They slow down when roles, decisions and responsibilities become unclear.\"",
      lede: "Wedo supports how organizations actually function, the development of leadership and the structuring of collectives in complex environments.",
      ctaConversation: "Start the conversation",
    },
  },

  about: {
    eyebrow: "William Marques · Founder",
    titleA: "Operations ",
    titleAccent: "before",
    titleB: " coaching.",
    lede: "Before supporting organizations, Wedo lived inside them. Twelve years in operations leadership. ExCom. Growth. Multi-site. International. An approach deeply anchored in reality.",
    keyFacts: [
      { num: "12+", label: "years in operations leadership" },
      { num: "70+", label: "employees supported through growth" },
      { num: "2,000+", label: "projects led · Top 40 clients & international firms" },
      { num: "FR · EN", label: "bilingual engagements" },
    ],
    bio: {
      eyebrow: "Journey",
      title: "Born of the ",
      titleAccent: "field.",
      p1: "Founder of Wedo Coaching, William Marques supports executives, ExComs, managers and organizations on leadership, collective functioning, transformation and managerial structuring.",
      p2: "Before dedicating himself to coaching, William spent over 12 years in operations leadership and business development roles, in environments blending growth, structuring, innovation and multi-site management.",
      p3: "He holds a Master's in Change Management through Innovation from ENSAM and held positions as Operations Director and ExCom member in a company at the crossroads of construction and technology. There, he supported the structuring of the organization, the development of new products and technical solutions, the coordination of multi-site teams and international expansion.",
      p4: "During this experience, he contributed to the company's growth from 4 to over 70 employees, leading initiatives on structuring, management, cross-functional coordination and operational transformation.",
      p5: "This field experience now nourishes a more concrete, more systemic coaching approach, deeply connected to the real life of organizations.",
    },
    intersections: {
      eyebrow: "At the intersection",
      title: "A company's challenges are never purely human or purely technical.",
      lede: "William works at the intersection of:",
      items: ["Leadership", "Collective functioning", "Communication", "Decisions", "Roles & responsibilities", "Organizational structuring"],
    },
    certifications: {
      eyebrow: "Education & Certifications",
      title: "A ",
      titleAccent: "rigorous",
      items: [
        { label: "Master Change Management through Innovation", org: "ENSAM Arts et Métiers" },
        { label: "Certified Professional Coach", org: "Institut Maïeutis" },
        { label: "Process Communication Model (PCM)", org: "Kahler Communication France certified" },
        { label: "12+ years in operations leadership", org: "Operations Director · ExCom member · Multi-site · International" },
      ],
    },
    testimonials: {
      eyebrow: "They talk about it",
      title: "What clients are saying.",
    },
    cta: {
      eyebrow: "Start the conversation",
      title: "A 30-minute first call to scope your need.",
    },
  },

  team: {
    eyebrow: "The team",
    titleA: "A collective of ",
    titleAccent: "expertise.",
    lede: "Wedo is no longer one person. Today it is a collective of certified experts, each with their own field, story and angle. Together, we support executives, ExComs and organizations to transform how they actually function.",
    cta: {
      eyebrow: "Work with Wedo",
      title: "The right coach for the right challenge. Every mission.",
    },
  },

  blog: {
    eyebrow: "Reflections",
    title: "The journal.",
    lede: "Convictions, field experience and observations on leadership, collective functioning, transformation and organizational structuring.",
    featured: "Featured",
    notFound: "Article not found.",
    allArticles: "All articles",
    furtherEyebrow: "To go further",
    furtherTitle: "Want to discuss your context?",
    backToBlog: "Back to journal",
    tocEyebrow: "Table of contents",
    tocTitle: "All articles",
    tocFooter: "Find these articles and many more on LinkedIn.",
  },

  newsletter: {
    title: "Receive new articles",
    lede: "A short notification with each new publication. No spam, no noise.",
    placeholder: "your@work.email",
    submit: "Subscribe",
    sending: "Sending…",
    success: "Thank you! You'll receive the next articles as they are published.",
    error: "Something went wrong. Please try again later.",
    privacy: "Your data stays confidential. Unsubscribe anytime.",
    subjectEmail: "[Newsletter] Subscription to Wedo journal",
    messageBody: "Newsletter subscription from the Wedo Coaching Blog page.",
  },

  resources: {
    eyebrow: "Resources",
    title: "Tools & guides.",
    lede: "A few practical tools to diagnose, structure and evolve the functioning of your organization.",
    empty: "No resource available yet.",
    download: "Download",
  },

  references: {
    eyebrow: "Case studies",
    titleA: "They placed their ",
    titleAccent: "trust in us.",
    lede: "An anonymized glimpse of missions led with executives, ExComs and organizations. Each engagement is custom-built; the examples below give a sense of the depth and diversity of our work.",
    empty: "No case study published yet.",
    ctaEyebrow: "A similar project?",
    ctaTitle: "Let's discuss your context and challenges.",
  },

  contact: {
    eyebrow: "Start a conversation",
    title: "Let's get straight to it.",
    lede: "A 30-minute first call to scope your need, understand your context, and see whether there's matter to work together. No commitment, no sales pitch.",
    sections: {
      coordinates: "Reach us",
      coordinatesTitle: "Get in touch with Wedo directly.",
      form: "Form",
      formTitle: "Describe your context.",
      calendar: "Book a meeting",
      calendarTitle: "Pick a slot directly in the calendar.",
      calendarLede: "Choose a 30-minute slot for a first call. Video or phone. This first conversation is free and without commitment.",
    },
    labels: {
      email: "Email",
      linkedin: "LinkedIn",
      interventions: "Engagements",
      interventionsValue: "France · Europe · International (FR / EN)",
    },
    form: {
      name: "Full name *",
      email: "Email *",
      company: "Company",
      phone: "Phone",
      subject: "Subject",
      message: "Your message *",
      success: "Message sent. We'll get back to you within 24 business hours.",
      error: "Something went wrong.",
    },
  },

  services: {
    common: {
      coreBlock: "At the heart of the engagement",
      coreTitle: "The topics we work on concretely.",
      formats: "Engagement formats",
      formatsTitle: "How we work.",
      ctaEyebrow: "Concretely?",
      ctaTitle: "A 30-minute first call to scope the need.",
    },
    codir: {
      eyebrow: "Executives & ExCom",
      titleA: "Align those who ",
      titleAccent: "decide.",
      lede: "An ExCom that works means: clear decisions, roles owned, and a voice that flows without filter. The rest follows.",
      sections: [
        {
          title: "The top conditions everything else.",
          body: [
            "In most growing organizations, operational tensions are rooted at the top: a blurry decision, a role not held, a topic that doesn't get addressed.",
            "Management absorbs it. Teams take the blow.",
            "Working on the ExCom means working on the lever that conditions the entire collective functioning.",
          ],
        },
        {
          title: "Observe. Clarify. Structure.",
          body: [
            "We start with a diagnostic of the real functioning: interviews, observation of governance bodies, reading of rituals, decision analysis, understanding of structural tensions.",
            "Then we support: roles, responsibilities, decision modes, collective rituals, and the ExCom's ability to function sustainably together.",
          ],
        },
      ],
      bullets: [
        "Governance and decision bodies",
        "Alignment and cooperation at the top",
        "Roles, responsibilities and trade-offs",
        "Cascading of decisions to management",
        "Regulation of structural tensions",
        "Diagnostic of collective functioning",
      ],
      formats: [
        { title: "Individual executive coaching", desc: "Regular support for the executive or ExCom member on posture, strategic decisions and role within the collective." },
        { title: "Collective ExCom engagement", desc: "6 to 12-month engagement: observation, structuring seminars, regulation workshops, follow-up of commitments." },
        { title: "Functioning diagnostic", desc: "Short diagnostic (4 to 6 weeks): interviews, observation, restitution. A structured outside reading of the real functioning." },
        { title: "Strategic facilitation", desc: "Facilitation of strategic seminars and key ExCom moments with upfront preparation and exit action plan." },
      ],
    },
    management: {
      eyebrow: "Management & Leadership",
      titleA: "Equip those who ",
      titleAccent: "carry.",
      lede: "Management is often the link that holds… or breaks. Posture, communication, autonomy, tensions, prioritization: we help managers hold their role over time.",
      sections: [
        {
          title: "From outstanding contributor to manager.",
          body: [
            "Becoming a manager isn't decreed. It's built. With tools, perspective, posture and a shared reference frame.",
            "We help managers shift from an expert or technical-referent posture to a team-leader posture: demanding, structuring, and deeply human.",
          ],
        },
        {
          title: "Hold the frame. Help them grow.",
          body: [
            "A good manager isn't the one who pleases. It's the one who helps teams progress, sets clear expectations, regulates tensions and empowers without delegating the role away.",
            "Our approach combines structured learning, role-plays, experience sharing and individual support to anchor transformations.",
          ],
        },
      ],
      bullets: [
        "Managerial posture and authority",
        "Delegation, empowerment, demand",
        "Communication, feedback, reframing",
        "Tension management and trade-offs",
        "Team animation and managerial rituals",
        "Holding difficult conversations",
      ],
      formats: [
        { title: "Individual managerial coaching", desc: "Regular 6 to 12-session engagement to anchor an aligned and demanding managerial posture." },
        { title: "Collective management journey", desc: "Module cycle (in-person + intersessions) for a cohort of managers: alignment, common language, shared standards." },
        { title: "Managerial co-development", desc: "Peer groups facilitated to solve real managerial situations with method and collective intelligence." },
        { title: "Targeted workshops", desc: "Half-day or full-day themed workshops: feedback, delegation, conflict management, team animation." },
      ],
    },
    ecole: {
      eyebrow: "Management Academy",
      titleA: "Grow ",
      titleAccent: "managers",
      titleB: " over the long run.",
      lede: "A management academy is not a series of trainings. It is a shared framework that allows an organization to develop its own managerial culture: shared language, common posture, management standards, ways of functioning and collective level of demand.",
      sections: [
        {
          title: "Build a common managerial culture.",
          body: [
            "We design custom managerial academies and journeys that durably align practices and managerial behaviors across the organization.",
            "The goal isn't only to train managers. It's to create a common framework, develop consistent practices, strengthen autonomy, smooth communication and structure a managerial culture that lasts in the company's real daily life.",
          ],
        },
        {
          title: "Journeys built for real life.",
          body: [
            "Our programs combine structuring modules, role-plays, co-development, intersessions, individual support and communication & management tools.",
            "Each journey is custom-built based on managers' maturity level, the organization's stakes, the existing culture and the transformations to support.",
          ],
        },
      ],
      bullets: [
        "Custom managerial academies",
        "Common framework and managerial culture",
        "Peer co-development",
        "Anchoring in real daily work",
        "Do.We · companion between sessions",
        "Optional individual follow-up",
      ],
    },
    coo: {
      eyebrow: "COO · Operational Advisory",
      titleA: "Structure the ",
      titleAccent: "growth.",
      lede: "When a company grows fast, the way it works often breaks before the people do. Blurry roles, chaotic coordination, decisions pushed too high, teams burning out: that's exactly where we step in.",
      sections: [
        {
          title: "An operator's perspective.",
          body: [
            "Before supporting companies, William drove the growth of an organization from 4 to over 70 employees as Operations Director and ExCom member.",
            "That field experience now feeds operational advisory deeply anchored in reality: structuring, management, growth, coordination, multi-site, transformation.",
          ],
        },
        {
          title: "Scaling is something you prepare.",
          body: [
            "Beyond a certain threshold, the system that made your success becomes the one that slows you down. Founder bottlenecks. Decisions moving up. Middle management without a frame to hold.",
            "We support the structuring: target organization, roles, managerial rituals, cross-functional coordination. Growth becomes an opportunity, not a risk.",
          ],
        },
        {
          title: "For whom?",
          body: [
            "Executives of growing SMEs. Multi-site structures. Organizations in transformation. Companies changing scale. Founders who have become decision bottlenecks.",
            "Interim CEOs, COOs and DGs mandated to deliver fast impact: post-LBO, crisis exit, succession, restructuring, scale-up. WeDo brings an operator's sparring to frame fast and hold the trajectory.",
          ],
        },
      ],
      bullets: [
        "Organizational structuring",
        "Roles, responsibilities and coordination",
        "Scaling and multi-site",
        "Managerial rituals and decision chain",
        "Executive strategic sparring",
        "Transition management · fast impact",
      ],
      formats: [
        { title: "Diagnostic mission", desc: "Short organizational diagnostic (4 to 8 weeks). Clear photograph of real functioning and prioritized structuring plan." },
        { title: "Fractional COO", desc: "Regular operational presence (1 to 2 days/week) to support structuring and steering over time." },
        { title: "Structuring mission", desc: "Project engagement (3 to 9 months) on a target transformation: organization, multi-site, international rollout, commercial structuring, etc." },
        { title: "Transition CEO / sparring partner", desc: "Regular support for an interim CEO, COO or DG mandated to deliver fast impact. Framing, structural decisions, crisis management, strategic communication." },
      ],
    },
    seminaires: {
      eyebrow: "Seminars & Transformation",
      titleA: "Seminars that ",
      titleAccent: "structure.",
      lede: "A useful seminar is not a parenthesis. It's a moment of collective acceleration: what gets said, what gets clarified, what gets committed to.",
      sections: [
        {
          title: "Working on what daily life doesn't allow.",
          body: [
            "We design transformation, strategic alignment and collective structuring seminars. For ExComs, leadership teams and groups that want to transform how they actually function — not just their cohesion.",
          ],
        },
        {
          title: "Experiential at the service of substance.",
          body: [
            "Some formats integrate experiential or immersive approaches, calibrated to the team's context. The medium changes. The subject stays: cooperation, quality of the collective and sustainable transformation of practices.",
          ],
        },
      ],
      bullets: [
        "ExCom seminars & transformation",
        "Strategic alignment and cooperation",
        "Collective intelligence",
        "Facilitation of key moments",
        "Upstream preparation & exit action plan",
        "Discreet, premium experiential formats",
      ],
    },
    pcm: {
      eyebrow: "PCM · Relational dynamics",
      titleA: "Understand how ",
      titleAccent: "we function.",
      lede: "Process Communication Model: a precise tool to decode relational dynamics, psychological needs and behaviors under stress. For yourself, for your teams, for your leadership.",
      badgeAlt: "PCM Accredited Coach · Process Communication Model",
      sections: [
        {
          title: "A tool of rare precision.",
          body: [
            "PCM doesn't classify people: it describes the diversity of ways to relate, communicate, and react when pressure rises. It's a leadership tool, not a personality test.",
            "PCM Accredited Coach by Kahler Communication France, William integrates PCM transversally across all Wedo engagements: executive coaching, managerial journeys, team seminars, ExCom support. PCM isn't a standalone offer, it's a reading grid that flows through all our interventions.",
          ],
        },
        {
          title: "Adapt your communication. Without betraying yourself.",
          body: [
            "PCM helps you know yourself better and adapt your communication to others without compromising, without manipulating. It's a powerful lever for managers, executives, sales, HR and all roles with high relational stakes.",
            "Beyond the individual, PCM transforms team dynamics: better tension regulation, common language, anticipation of behaviors under stress.",
          ],
        },
      ],
      bullets: [
        "Individual personality inventory",
        "Decoding of psychological needs",
        "Understanding of behavior under stress",
        "Adapting one's communication",
        "Relational leadership",
        "Team cohesion and dynamics",
        "Management tailored to each profile",
        "Client / sales communication",
        "Recruitment and onboarding",
      ],
      formats: [
        { title: "PCM team", desc: "Team workshop (1 to 2 days) to map the collective dynamic and install a common language." },
        { title: "PCM in coaching", desc: "Integration of PCM into individual coaching: particularly useful for executives and managers stepping into new roles." },
        { title: "PCM ExCom", desc: "Dedicated format for executive committees: mapping, regulation, collective leadership." },
        { title: "PCM in managerial journeys", desc: "PCM module integrated into Wedo Management Academy programs to anchor a common language." },
      ],
    },
    rise: {
      eyebrow: "RISE · Reserved premium experience",
      eyebrowDiscreet: "Going further · By invitation",
      titleDiscreet: "RISE — the experience reserved for a select circle.",
      ledeDiscreet: "A rare immersive format, designed for executives and ExCom members who want to work in depth on their posture, strategic lucidity and ability to last. Away from daily life. Off-catalogue.",
      discoverDiscreet: "Discover RISE",
      titleA: "Step ",
      titleAccent: "above.",
      titleB: " Truly.",
      lede: "RISE is an immersive experience designed for executives, entrepreneurs and leaders who feel the need to step out of operational rhythm to rework their vision, posture, lucidity, energy and ability to last in demanding environments. The experiential medium creates the conditions for stepping back. The real subject stays leadership.",
      sections: [
        {
          title: "Rare. Exclusive. Transformational.",
          body: [
            "RISE is not an open seminar. Each immersion gathers a limited number of executives ready to work in depth on their real functioning and strategic stakes.",
            "Custom individual format or small peer circle (5 to 8 executives). Location, rhythm and intensity designed for the situation.",
          ],
        },
      ],
      bullets: [
        "Strategic stepping back",
        "Vision, posture, energy",
        "Immersive & demanding environment",
        "Individual or collective format (5-8)",
        "Integrated individual coaching",
        "Exit action plan",
      ],
      formats: [
        { title: "Individual RISE", desc: "Custom immersion for an executive. Private frame, rhythm and themes calibrated. Maximum intensity." },
        { title: "Collective RISE", desc: "Small peer circle (5 to 8 executives). Crossed views, demanding dynamic, individualized action plan." },
      ],
    },
  },

  flowProgram: {
    eyebrow: "FLOW · Autonomous program",
    titleA: "90 days to ",
    titleAccent: "find your rhythm again.",
    lede: "FLOW is our autonomous personal-piloting program. 6 modules, 90 days, built from over 200 field coaching sessions. For executives and managers who want to last over time — without burning out, without over-adapting.",
    bullets: [
      "Clarity of personal functioning",
      "Energy & regulation under pressure",
      "Posture & assumed boundaries",
      "Concrete exit action plan",
    ],
    cta: "Discover FLOW",
    baseline: "Clarity · Energy · Posture · For the long run.",
    modules: [
      { n: "01", t: "Personal manual" },
      { n: "02", t: "Energy & rhythm" },
      { n: "03", t: "Stress & automatisms" },
      { n: "04", t: "Saying things clearly" },
      { n: "05", t: "Posture & limits" },
      { n: "06", t: "Last over time" },
    ],
    footer: "200+ field sessions",
  },

  doWeJourney: {
    badge: "Anonymized example · every journey is custom-built",
    eyebrow: "Manager journey · 9 months",
    titleA: "A shared frame.",
    titleAccent: "Rising standards.",
    lede: "Every journey is calibrated for the client: duration, modules, rhythm, themes, audiences. Here is a representative example of a 9-month architecture for a manager cohort.",
    milestones: [
      { when: "Month 1", label: "Kick-off seminar", desc: "Shared frame, cohort contract, starting posture." },
      { when: "Months 2 → 4", label: "Module A · Self-awareness", desc: "Understand your functioning and impact in the managerial role." },
      { when: "Months 4 → 6", label: "Module B · Posture & communication", desc: "Message clarity, feedback quality, right distance." },
      { when: "Months 6 → 8", label: "Module C · Collective performance", desc: "Cooperation, delegation, team autonomy." },
      { when: "Month 9", label: "Certification & anchoring", desc: "Tripartite review, individual action plan, optional coaching." },
    ],
    footerNote: "Representative document",
    footerText: "Supervised then autonomous co-development between modules. Optional individual coaching. Tripartite review (manager / N+1 / HR).",
    dowe: {
      badge: "Do.We · Continuous learning · In development",
      eyebrow: "Support that continues between sessions",
      lede: "Managerial transformations don't happen only during workshops or seminars. Do.We extends the work engaged during the programs: guided reflection, prioritization support, preparation of sensitive situations, anchoring of practices, pedagogical continuity, managerial reflection. The goal: strengthen autonomy and foster sustainable progression in the real daily life of management.",
      chatTitle: "Do.We · Wedo Companion",
      chatStatus: "ONLINE",
      userMsg: "I need to give difficult feedback to a team member tomorrow. How do I prepare?",
      botLabel: "Guided reflection",
      botMsg: "A few questions to prepare this conversation:",
      botList: ["Which specific behavior do you want to address?", "What concrete impact does it have on the team?", "What outcome do you expect from the conversation?"],
      botNote: "You can then build on the framework seen with William in Module B.",
      placeholder: "Write a message to Do.We…",
      confidential: "Confidential",
      quote: "\"Not to replace humans. To extend the coach between sessions.\"",
      quoteAuthor: "· Wedo",
    },
  },

  langSwitcher: {
    soonTitle: "FR · Bientôt",
    soonDesc: "The French version is the original. EN version is now available.",
  },
};

export default en;
